import { useCallback, useState } from 'react';

import type { UseMutationResult } from '@tanstack/react-query';

import { useBoolean } from './use-boolean';

interface UseBulkDeleteProps {
  resetSelection: () => void;
  deleteMutation: UseMutationResult<
    unknown,
    unknown,
    {
      id: string;
    },
    unknown
  >;
  invalidateQueryAllObject: () => void;
}

export function useBulkDelete({ deleteMutation, invalidateQueryAllObject, resetSelection }: UseBulkDeleteProps) {
  const [isBulkDeleting, setIsBulkDelete] = useBoolean(false);
  const [hasIssues, setHasIssues] = useState(0);

  const handleBulkDelete = useCallback(
    async (ids: number[]) => {
      let promised: Promise<unknown>[] = [];
      try {
        setIsBulkDelete.setTrue();
        promised = ids.map((id) => deleteMutation.mutateAsync({ id: id.toString() }));
        await Promise.all(promised);
      } catch {
        promised.forEach((promise) => {
          promise.catch(() => {
            setHasIssues((prev) => prev + 1);
          });
        });
      } finally {
        setIsBulkDelete.setFalse();
        invalidateQueryAllObject();
        resetSelection();
      }
    },
    [deleteMutation, invalidateQueryAllObject, resetSelection, setHasIssues, setIsBulkDelete]
  );

  return {
    handleBulkDelete,
    isBulkDeleting,
    hasIssues,
  };
}
